import {
  adoptFeatures,
  describe,
  featureCollection,
  fillStyle,
  lineFeature,
  polygonFeature,
  strokeStyle,
} from '../../common/geojson';

/**
 * The layers CSqueeze and EUSCoast share. Both measure land cover along transects over the same
 * two years, and differ only in what they call the classes, so they export the same shapes.
 * @module LANDCOVER_GEOJSON
 */

/**
 * @constant LAND_COVER_CLASSES
 * @description The five classes both modules reduce their imagery to, by the code the
 * classification carries. Written out by name as well as by code: a GIS user opening the file has
 * no way to know that 0 means mangrove, and the numbers are what the analysis actually stores.
 * @see {getLandCoverLabelMapBiomas}
 * @memberof module:LANDCOVER_GEOJSON
 */
export const LAND_COVER_CLASSES = {
  0: 'mangrove',
  1: 'vegetation',
  2: 'water',
  3: 'human',
  4: 'beach',
};

/**
 * @constant CLASS_NAMES
 * @description Readable names for the five classes, for the `name` a GIS shows in its attribute
 * table and its layer list. English, like the layer names beside them: the file travels away from
 * the app and should read the same wherever it is opened, whatever language it was made in.
 * @memberof module:LANDCOVER_GEOJSON
 */
const CLASS_NAMES = {
  mangrove: 'Mangrove',
  vegetation: 'Vegetation',
  water: 'Water',
  human: 'Human intervention',
  beach: 'Beach, dune and sand',
};

/**
 * @constant CLASS_COLORS
 * @description The colours the app paints each class with on the map, so a viewer that reads
 * simplestyle opens the file looking like the analysis did.
 * @memberof module:LANDCOVER_GEOJSON
 */
const CLASS_COLORS = {
  mangrove: '#687537',
  vegetation: '#129912',
  water: '#0000ff',
  human: '#af2a2a',
  beach: '#ffa07a',
};

/**
 * @constant SQUEEZE_COLORS
 * @description The verdict colours the map gives a transect, as hex: simplestyle asks for hex, and
 * the analysis names them the way Google Maps takes them.
 * @memberof module:LANDCOVER_GEOJSON
 */
const SQUEEZE_COLORS = {
  red: '#d32f2f',
  orange: '#f57c00',
  green: '#388e3c',
  gray: '#9e9e9e',
};

/**
 * @constant BASELINE_COLOR
 * @description The baseline's colour on the map.
 * @memberof module:LANDCOVER_GEOJSON
 */
const BASELINE_COLOR = '#ffd700';

/**
 * @constant AREA_COLOR
 * @description The area of interest's outline colour on the map.
 * @memberof module:LANDCOVER_GEOJSON
 */
const AREA_COLOR = '#00a391';

/**
 * @function periodName
 * @description What a period is called in the file. CSqueeze and EUSCoast label a period the user
 * picked twice in one year by its own `DD/MM/YY - DD/MM/YY` range, because the `2026-1` key says
 * nothing about which of the two it is; a period that is alone in its year keeps the year, and
 * every other module's period is named the same way. The label is passed in rather than derived
 * here, so this file holds no rule about when a year is spelled out - a module that does not
 * supply a map simply gets its keys back unchanged.
 * @param {String|Number} period - The period key, e.g. `2026` or `2026-1`.
 * @param {Object} [periodLabels] - `{periodKey: label}`, as `state.<module>.periodLabels` holds it.
 * @memberof module:LANDCOVER_GEOJSON
 * @returns {String|Number} The label when there is one, the key otherwise.
 */
export const periodName = (period, periodLabels) => periodLabels?.[period] ?? period;

/**
 * @function landCoverLayer
 * @description Layer name for one period's land cover, e.g. `land_cover_2018` or
 * `land_cover_01/02/26 - 01/03/26`.
 * Each period is its own layer rather than one layer with a year column: the whole point of this
 * analysis is the difference between two periods, and a GIS compares two layers far more readily
 * than one layer filtered two ways - they can be styled apart, stacked, and toggled against each
 * other. The key stays on the features as well, so either way of working still works.
 * @param {String|Number} period - The period, named by {@link periodName}.
 * @memberof module:LANDCOVER_GEOJSON
 * @returns {String} The layer name.
 */
export const landCoverLayer = (period) => `land_cover_${period}`;

/**
 * @function waterLayer
 * @description Layer name for one period's water, e.g. `water_2018`.
 * @param {String|Number} period - The period, named by {@link periodName}.
 * @memberof module:LANDCOVER_GEOJSON
 * @returns {String} The layer name.
 */
export const waterLayer = (period) => `water_${period}`;

/**
 * @function landCoverFeatures
 * @description The classified land cover of one period, one feature per class.
 * The analysis holds each class as `{id, year, geometry}` - its own wrapper, not a GeoJSON
 * feature - so the geometry is taken out of it and the class is written alongside as both the code
 * the analysis uses and the name it stands for.
 * The layer and the names are spelled with the period's label, while `year` keeps the key the
 * analysis works in: a reader opening the file sees the dates, and anything that filters or joins
 * on the year still finds what it expects.
 * A year that is held as a collection instead, rather than as that list, is taken as it comes: it
 * carries no class of its own to name, and losing the layer over the difference is worse than
 * losing the labels.
 * @param {Array<Object>|Object} [vectors] - The period's classes, or a collection of them.
 * @param {String|Number} year - The period key they belong to.
 * @param {Object} [periodLabels] - `{periodKey: label}`, as `state.<module>.periodLabels` holds it.
 * @memberof module:LANDCOVER_GEOJSON
 * @returns {Array<Object>} The features.
 */
export const landCoverFeatures = (vectors, year, periodLabels) => {
  if (!vectors) return [];
  if (!Array.isArray(vectors)) {
    const period = periodName(year, periodLabels);
    return adoptFeatures(vectors, landCoverLayer(period), {
      year,
      period,
      ...describe(`Land cover ${period}`),
    });
  }

  return vectors.flatMap((entry) => {
    const entryYear = entry?.year ?? year;
    const period = periodName(entryYear, periodLabels);
    const className = LAND_COVER_CLASSES[entry?.id] ?? null;
    const label = className ? `${CLASS_NAMES[className]} ${period}` : `Land cover ${period}`;
    return adoptFeatures(entry?.geometry, landCoverLayer(period), {
      year: entryYear,
      period,
      class_code: entry?.id,
      class: className,
      ...describe(label, `Land cover classified for ${period}`),
      ...fillStyle(CLASS_COLORS[className] || '#808080'),
    });
  });
};

/**
 * @function baselineFromOverlay
 * @description Reads a baseline's coordinates off the Google Maps polyline it is drawn as. These
 * two modules keep the drawn overlay rather than the coordinates behind it, so the path has to be
 * walked to get at them - the same way their session export does.
 * @param {Object} [baseLine] - `{overlay: google.maps.Polyline}`, or a falsy value if nothing is drawn.
 * @memberof module:LANDCOVER_GEOJSON
 * @returns {Array<Array<Number>>|null} `[lng, lat]` pairs, or `null`.
 */
export const baselineFromOverlay = (baseLine) => {
  try {
    if (!baseLine?.overlay?.getPath) return null;
    return baseLine.overlay
      .getPath()
      .getArray()
      .map((coord) => [coord.lng(), coord.lat()]);
  } catch (error) {
    console.error('[GeoJSON] could not read the baseline overlay', error);
    return null;
  }
};

/**
 * @function transectFeatures
 * @description The transects, each drawn as one line across the baseline, carrying whatever the
 * analysis concluded about it. They are held as two sides measured out from the baseline, which is
 * how the analysis uses them; joined end to end they are the single line the user sees on the map
 * and expects in a GIS.
 * @param {Array<Object>} [transectsData] - `{transect, sides: [{coords}, {coords}]}` entries.
 * @param {Object} [attributes] - Per-transect attributes, keyed by transect number - the squeeze
 *  verdict, for CSqueeze. Left out, the transects go out as plain geometry.
 * @memberof module:LANDCOVER_GEOJSON
 * @returns {Array<Object|null>} The features.
 */
export const transectFeatures = (transectsData, attributes = {}) =>
  (transectsData || []).map((transect) =>
    lineFeature([...(transect?.sides?.[0]?.coords || []), ...(transect?.sides?.[1]?.coords || [])], 'transects', {
      transect: transect?.transect,
      ...transectLabel(transect, attributes?.[transect?.transect]),
      ...(attributes?.[transect?.transect] || {}),
    }),
  );

/**
 * @function transectLabel
 * @description What a transect is called, and how it is drawn: its number, the verdict it was
 * given, and the colour the map gave it for that verdict. A transect labelled only "LineString"
 * tells a reader nothing about which of the thirty it is or what was concluded there.
 * @param {Object} transect - The transect.
 * @param {Object} [verdict] - Its squeeze attributes, when the analysis reached one.
 * @memberof module:LANDCOVER_GEOJSON
 * @returns {Object} Label and style properties.
 */
const transectLabel = (transect, verdict) => ({
  ...describe(`Transect ${transect?.transect}`, verdict?.squeeze_label || undefined),
  ...strokeStyle(SQUEEZE_COLORS[verdict?.squeeze_color] || '#00a391', { width: 3 }),
});

/**
 * @function buildLandCoverGeoJson
 * @description Gathers a finished land-cover analysis into one FeatureCollection: the area, the
 * baseline, the transects, and the classified land cover and water of each period as a layer of
 * its own, so the two periods can be styled against each other in a GIS rather than filtered
 * apart. Every feature also carries a readable `name`, so the attribute table says what it is
 * holding without anyone having to learn the class codes.
 * @param {Object} params
 * @param {Array<Array<Number>>} [params.coordinates] - Area of interest ring, `[lng, lat]` pairs.
 * @param {Array<Array<Number>>} [params.baselineCoords] - The baseline as `[lng, lat]` pairs, used
 *  in preference to the overlay: it is what the analysis was handed, and it survives the overlay.
 * @param {Object} [params.baseLine] - The drawn baseline overlay, read only as a fallback.
 * @param {Array<Object>} [params.transectsData] - Generated transects.
 * @param {Object} [params.transectsAttributes] - Per-transect attributes to carry on them.
 * @param {Object} [params.classLandCoversVectors] - Classified land cover, keyed by period.
 * @param {Array<Object>} [params.waterLandCoverVectors] - `{year, geometry}` entries for water.
 * @param {Object} [params.periodLabels] - `{periodKey: label}`, as
 *  `state.<module>.periodLabels` holds it. Passed by the two modules that label their periods; left
 *  out, the periods are written under the keys the analysis holds them by.
 * @memberof module:LANDCOVER_GEOJSON
 * @returns {Object} A FeatureCollection, empty when the analysis has produced nothing yet.
 */
export const buildLandCoverGeoJson = ({
  coordinates,
  baselineCoords,
  baseLine,
  transectsData,
  transectsAttributes,
  classLandCoversVectors,
  waterLandCoverVectors,
  periodLabels,
}) =>
  featureCollection([
    polygonFeature(coordinates, 'area_of_interest', {
      ...describe('Area of interest'),
      ...fillStyle(AREA_COLOR, 0),
    }),
    lineFeature(baselineCoords?.length ? baselineCoords : baselineFromOverlay(baseLine), 'baseline', {
      ...describe('Baseline'),
      ...strokeStyle(BASELINE_COLOR, { width: 3 }),
    }),
    transectFeatures(transectsData, transectsAttributes),
    Object.entries(classLandCoversVectors || {}).flatMap(([year, vectors]) =>
      landCoverFeatures(vectors, year, periodLabels),
    ),
    (waterLandCoverVectors || []).flatMap((water) => {
      const period = periodName(water?.year, periodLabels);
      return adoptFeatures(water?.geometry, waterLayer(period), {
        year: water?.year,
        period,
        class_code: 2,
        class: LAND_COVER_CLASSES[2],
        ...describe(`${CLASS_NAMES.water} ${period}`, `Water extent in ${period}`),
        ...fillStyle(CLASS_COLORS.water),
      });
    }),
  ]);
